import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./ISOImplementation.module.css";
import { Service } from "../../../types";

interface ServicesProps {
  services: Service[];
}

const ISOImplementation: React.FC<ServicesProps> = ({ services }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Number of visible cards
  const visibleCards = 3;

  // Calculate total slides
  const totalSlides = Math.ceil(services.length / visibleCards);

  const startAutoRotate = useCallback(() => {
    stopAutoRotate();
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    }, 4000); // Rotate every 4 seconds
  }, [totalSlides]);

  // Reset interval when isPaused changes
  useEffect(() => {
    if (!isPaused) {
      startAutoRotate();
    } else {
      stopAutoRotate();
    }

    return () => {
      stopAutoRotate();
    };
  }, [isPaused, currentIndex, startAutoRotate]);

  const stopAutoRotate = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Get services for current slide
  const getVisibleServices = () => {
    const startIndex = currentIndex * visibleCards;
    const endIndex = startIndex + visibleCards;
    const visibleServices = services.slice(startIndex, endIndex);

    // If we don't have enough services for the last slide, fill with duplicates from start
    if (visibleServices.length < visibleCards) {
      return [
        ...visibleServices,
        ...services.slice(0, visibleCards - visibleServices.length),
      ];
    }

    return visibleServices;
  };

  const visibleServices = getVisibleServices();

  return (
    <section id="primary-services" className={styles.equipmentServices}>
      <div className="sub-container">
        <div className={styles.sectionTitle}>
          <h2 className={styles.sectionTitleH2}>ISO Implementation</h2>
        </div>
        {/* Carousel Container */}
        <div
          className={styles.carouselContainer}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={carouselRef}
        >
          {/* Navigation Buttons */}
          {/* <button
            className={styles.carouselButtonPrev}
            onClick={prevSlide}
            aria-label="Previous services"
          >
            <i className="fas fa-chevron-left" />
          </button> */}

          {/* Carousel Track */}
          <div className={styles.carouselTrack}>
            <div
              className={styles.carouselInner}
              style={{
                transform: `translateX(-${
                  currentIndex * (100 / visibleCards)
                }%)`,
                transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {/* We render all services for smooth sliding */}
              {services.map((service, index) => (
                <div
                  key={`${service.id}-${index}`}
                  className={`${styles.serviceCard} ${
                    visibleServices.some(
                      (s) =>
                        s.id === service.id &&
                        visibleServices.indexOf(service) ===
                          services.indexOf(service),
                    )
                      ? styles.active
                      : ""
                  }`}
                >
                  <div className={styles.serviceImageContainer}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className={styles.serviceImage}
                    />
                    <div className={styles.serviceOverlay}>
                      <div className={styles.overlayContent}>
                        <h3>{service.title}</h3>
                        <p>{service.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* <button
            className={styles.carouselButtonNext}
            onClick={nextSlide}
            aria-label="Next services"
          >
            <i className="fas fa-chevron-right" />
          </button> */}
        </div>

        {/* Slide Indicators */}
        {/* <div className={styles.slideIndicators}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`${styles.slideIndicator} ${
                index === currentIndex ? styles.active : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div> */}
      </div>
    </section>
  );
};

export default ISOImplementation;
